/*
 * The agenda block in an invite body. It has the same format as the block the
 * backend's produce_agenda tool writes when AiVA drafts an agenda in Chat
 * (apps/backend/ai/integrations/systemic_tools/submit_tools/produce_agenda_tool.py),
 * so an agenda saved here and one drafted in Chat replace each other. The block
 * sits at the top of the body. The rest of the body, including the Teams join
 * details, is kept.
 */

const AGENDA_START = "AIVA_AGENDA_START";
const AGENDA_END = "AIVA_AGENDA_END";
/** The block's heading. Outlook drops HTML comments, so the heading is how a saved block is found again. */
const HEADING_RE = /^(?:\u{1F4CB}\s*)?Agenda \(AIVA\)$/u;
/** The line of underscores that starts the Teams join details in older invites. */
const TEAMS_SEPARATOR_RE = /_{10,}/;
/**
 * Where the Teams join details start, as plain text: the older invites'
 * underscore line, or the newer block's own heading ("Microsoft Teams meeting",
 * "Microsoft Teams Need help?") or its join line. Each heading must be a whole
 * line, so a sentence that merely mentions Teams is kept.
 */
const TEAMS_BLOCK_START_RES: readonly RegExp[] = [
  TEAMS_SEPARATOR_RE,
  /^[ \t]*Microsoft Teams(?: meeting| Need help\?)?[ \t]*$/im,
  /^[ \t]*Join the meeting now[ \t]*$/im,
  /^[ \t]*Join:?[ \t]*https:\/\/teams\.microsoft\.com\//im,
];

/** Invite text without the Teams join details that Teams adds at its end. */
export function withoutTeamsBlock(text: string): string {
  let cut = text.length;
  for (const re of TEAMS_BLOCK_START_RES) {
    const match = re.exec(text);
    if (match && match.index < cut) cut = match.index;
  }
  return text.slice(0, cut).trim();
}
/** Enough for any real invite; stops a malformed body from looping. */
const MAX_BLOCKS_SCRUBBED = 5;

const BLOCK_TAGS = new Set([
  "P", "DIV", "LI", "UL", "OL", "TR", "TABLE", "H1", "H2", "H3", "H4", "H5", "H6", "BLOCKQUOTE", "PRE",
]);
const SKIPPED_TAGS = new Set(["HEAD", "STYLE", "SCRIPT", "TITLE"]);

/** Python's html.escape, which the backend uses on a plain-text agenda. */
function escapeHtml(text: string): string {
  return text
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#x27;");
}

/** The backend's _agenda_html, for a plain-text agenda. */
function agendaBlockHtml(agenda: string): string {
  return (
    `<!--${AGENDA_START}-->\n` +
    `<div style="font-family:'Segoe UI',Arial,sans-serif;color:#242424;">\n` +
    `<p style="font-weight:600;">\u{1F4CB} Agenda (AIVA)</p>\n` +
    `<div>${escapeHtml(agenda).replace(/\n/g, "<br>\n")}</div>\n` +
    `</div>\n` +
    `<!--${AGENDA_END}-->`
  );
}

/** Readable text from HTML: line breaks at <br> and block elements, list items numbered or bulleted. */
function textOf(nodes: readonly Node[]): string {
  let out = "";
  const walk = (node: Node) => {
    if (node.nodeType === Node.TEXT_NODE) {
      out += (node.textContent ?? "").replace(/\s+/g, " ");
      return;
    }
    if (node.nodeType !== Node.ELEMENT_NODE) return;
    const el = node as Element;
    if (SKIPPED_TAGS.has(el.tagName)) return;
    if (el.tagName === "BR") {
      out += "\n";
      return;
    }
    const block = BLOCK_TAGS.has(el.tagName);
    if (block && out && !out.endsWith("\n")) out += "\n";
    if (el.tagName === "LI") {
      const siblings = el.parentElement ? Array.from(el.parentElement.children).filter((c) => c.tagName === "LI") : [];
      out += el.parentElement?.tagName === "OL" ? `${siblings.indexOf(el) + 1}. ` : "- ";
    }
    el.childNodes.forEach(walk);
    if (block && !out.endsWith("\n")) out += "\n";
  };
  nodes.forEach(walk);
  return out
    .split("\n")
    .map((line) => line.trim())
    .join("\n")
    .replace(/\n{3,}/g, "\n\n")
    .trim();
}

const trimmedText = (el: Element) => (el.textContent ?? "").trim();

/**
 * The agenda block's heading, lifted to the outermost element that holds only
 * the heading (e.g. the <p> around a <span>). Null when the body has no block.
 */
function findHeading(doc: Document): Element | null {
  for (const el of Array.from(doc.body.querySelectorAll("*"))) {
    if (!HEADING_RE.test(trimmedText(el))) continue;
    if (Array.from(el.children).some((c) => HEADING_RE.test(trimmedText(c)))) continue;
    let heading = el;
    while (heading.parentElement && heading.parentElement !== doc.body && trimmedText(heading.parentElement) === trimmedText(el)) {
      heading = heading.parentElement;
    }
    return heading;
  }
  return null;
}

/**
 * What belongs to the block: the heading and the agenda after it. When the block
 * still has its own wrapper (the heading and one agenda element), that wrapper
 * is the block. Otherwise the block is only the heading and the element after
 * it, so nothing else in the body can be taken with it.
 */
function blockParts(heading: Element): { remove: Element[]; agenda: Element[] } {
  const next = heading.nextElementSibling;
  const wrapper = heading.parentElement;
  const agenda = next ? [next] : [];
  if (
    wrapper &&
    wrapper.tagName !== "BODY" &&
    wrapper.children.length <= 2 &&
    !TEAMS_BLOCK_START_RES.some((re) => re.test(wrapper.textContent ?? ""))
  ) {
    return { remove: [wrapper], agenda };
  }
  return { remove: [heading, ...agenda], agenda };
}

function parse(html: string): Document {
  return new DOMParser().parseFromString(html, "text/html");
}

/** Remove the sentinel comments the backend writes (Outlook usually drops them anyway). */
function removeSentinels(doc: Document): void {
  const walker = doc.createTreeWalker(doc.documentElement, NodeFilter.SHOW_COMMENT);
  const comments: Comment[] = [];
  for (let n = walker.nextNode(); n; n = walker.nextNode()) {
    const data = (n.textContent ?? "").trim();
    if (data === AGENDA_START || data === AGENDA_END) comments.push(n as Comment);
  }
  comments.forEach((c) => c.remove());
}

/** Remove one element, and the <br> the backend puts after its block. */
function removeWithTrailingBreak(el: Element): void {
  let after = el.nextSibling;
  while (after?.nodeType === Node.TEXT_NODE && !(after.textContent ?? "").trim()) after = after.nextSibling;
  if (after?.nodeType === Node.ELEMENT_NODE && (after as Element).tagName === "BR") after.remove();
  el.remove();
}

/** Take every agenda block out of the body. */
function scrubAgenda(doc: Document): void {
  removeSentinels(doc);
  for (let i = 0; i < MAX_BLOCKS_SCRUBBED; i++) {
    const heading = findHeading(doc);
    if (!heading) return;
    const { remove } = blockParts(heading);
    remove.forEach(removeWithTrailingBreak);
  }
}

export interface InviteContent {
  /** The agenda block's text, or null when the invite has none. */
  readonly agenda: string | null;
  /** The invite's own text, without the agenda block or the Teams join details. */
  readonly text: string;
}

/** Split an invite body (HTML) into its agenda block and its own text. */
export function readInvite(html: string): InviteContent {
  const doc = parse(html);
  const heading = findHeading(doc);
  const agenda = heading ? textOf(blockParts(heading).agenda) : null;
  scrubAgenda(doc);
  return { agenda, text: withoutTeamsBlock(textOf([doc.body])) };
}

/**
 * The body with `agenda` as its agenda block: any earlier block is removed and
 * the new one put at the top. An empty agenda removes the block.
 */
export function mergeAgenda(html: string, agenda: string): string {
  const doc = parse(html);
  scrubAgenda(doc);
  const text = agenda.trim();
  if (text) doc.body.insertAdjacentHTML("afterbegin", `${agendaBlockHtml(text)}\n<br>\n`);
  return doc.documentElement.outerHTML;
}

/** Readable plain text from an HTML fragment (line breaks kept, list items numbered or bulleted). */
export function htmlToText(html: string): string {
  return textOf([parse(html).body]);
}

/** Tags that show a reply is HTML rather than plain text or Markdown. */
const HTML_RE = /<\/?(p|ul|ol|li|br|div|h[1-6]|strong|b|em|table)\b/i;

/**
 * An AI-written agenda as plain lines for the Agenda box: HTML is turned into
 * text, and Markdown emphasis and headings are dropped (list markers stay).
 */
export function agendaFromReply(reply: string): string {
  const text = HTML_RE.test(reply) ? htmlToText(reply) : reply;
  return text
    .split("\n")
    .map((line) =>
      line
        .replace(/^\s*#{1,6}\s+/, "")
        .replace(/(\*\*|__)(.+?)\1/g, "$2")
        .trimEnd(),
    )
    .join("\n")
    .replace(/\n{3,}/g, "\n\n")
    .trim();
}

/** The agenda's items: its non-empty lines. */
export function agendaItemCount(agenda: string): number {
  const lines = agenda
    .split("\n")
    .map((line) => line.trim())
    .filter(Boolean);
  // Headings ("Purpose", "Key points") aren't items: when the agenda has list
  // items, only they count. A plain list with no markers counts every line, as in the mockup.
  const listed = lines.filter((line) => LIST_ITEM_RE.test(line)).length;
  return listed > 0 ? listed : lines.length;
}

/** A list item: "- …", "• …", "* …", "1. …" or "1) …". */
export const LIST_ITEM_RE = /^\s*(?:\d+[.)]|[-•*])\s+\S/;
